/*
 * Front-end behaviour.
 *
 * Lifted out of the Blade layout. Everything here is static - no route, token
 * or setting is interpolated into it - which is exactly why it could move: a
 * bundled file cannot read Blade variables.
 *
 * Anything that does need a value from PHP stays in its own view and reads it
 * from a data attribute; the search box and the announcement bar work that way.
 */

/*
 * Bootstrap's JavaScript, bundled - and only the three pieces this site uses.
 *
 * It came from cdn.jsdelivr.net as one render-blocking <script> on every page:
 * a DNS lookup, a connection and a TLS handshake to an origin nothing else on
 * the page needed, before the first paint. The stylesheet next door was
 * bundled for exactly that reason years ago; the script half was simply never
 * finished.
 *
 * Two imports rather than the bundle, because two is all a reader needs - the
 * search dialog and the mobile menu. The full bundle carries carousels,
 * tooltips, toasts, offcanvas and the rest, none of which appears anywhere in
 * resources/views/frontend.
 *
 * Importing a component registers its data-attribute handlers, so
 * data-bs-toggle keeps working with no code of our own. window.bootstrap is
 * set because this file opens the search dialog by hand further down, and
 * because it is what every snippet anybody pastes will reach for.
 */
import Modal from 'bootstrap/js/dist/modal';
import Collapse from 'bootstrap/js/dist/collapse';

window.bootstrap = Object.assign(window.bootstrap || {}, { Modal, Collapse });

/*
 * The account menu is fetched, not bundled.
 *
 * It is the only dropdown on the front end and it sits inside @auth - so the
 * one person who ever sees it is whoever is signed in to write the articles.
 * Bootstrap's Dropdown drags Popper in with it, and bundling the pair put
 * about 10KB of positioning engine in front of every reader for a menu they
 * will never be shown.
 *
 * Vite splits this into a chunk of its own and the browser asks for it only on
 * a page that has a dropdown on it.
 */
if (document.querySelector('[data-bs-toggle="dropdown"]')) {
    import('bootstrap/js/dist/dropdown').then(function (module) {
        window.bootstrap.Dropdown = module.default;
    });
}

/*
 * The pre-paint reveal guard is NOT here. It sets .js-anim before the body is
 * parsed, and Vite serves this as a module script, which is deferred - the
 * class would land after the content had already been painted, so the page
 * would show and then animate. It stays inline in the layout head.
 */

// The masthead picks up a hairline lift once the page has moved.
    // rAF-throttled and passive, so scrolling stays off the main thread.
    (function () {
        var nav = document.querySelector('.glass-nav');
        if (!nav) return;

        var ticking = false;

        function update() {
            ticking = false;
            nav.classList.toggle('scrolled', window.scrollY > 50);
        }

        window.addEventListener('scroll', function () {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
        }, { passive: true });

        update();
    })();

/*
     * Motion.
     *
     * Two jobs: reveal content as it scrolls into view, and thread a
     * progress bar across the top of an article. Both are additive - if
     * anything here fails the page is simply static.
     *
     * The .js-anim class was set in <head> before first paint, so nothing
     * ever flashes visible and then hides.
     */
    (function () {
        if (!document.documentElement.classList.contains('js-anim')) return;

        // ------------------------------------------------ reveal
        var SELECTOR = [
            '.post-card',
            '.sidebar-card',
            '.related-post-card',
            '.comment-card',
            '.article-header',
            '.article-featured-img',
            '.article-video',
            '.author-box',
            '.tw-ad-wrapper',
            '.tw-news-card-wrapper',
            '.tw-more-news',
            // A promo code or affiliate link card. Its rows arrive one after
            // another once it does - see .offer-row in the stylesheet.
            '.offer'
        ].join(',');

        /*
         * Only what is still below the fold.
         *
         * By the time this module runs the first screen has already been
         * painted. Hiding it again to fade it back in made the reader watch it
         * blink, and pushed Largest Contentful Paint - the hero image, or the
         * first card on a listing - back by the length of the transition.
         */
        var fold = window.innerHeight || document.documentElement.clientHeight;

        var targets = Array.prototype.slice.call(document.querySelectorAll(SELECTOR))
            .filter(function (el) {
                return el.getBoundingClientRect().top >= fold;
            });

        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;

                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);

                // Drop the compositor hint once the transition has run, so a
                // long page is not left holding a layer per card.
                entry.target.addEventListener('transitionend', function handler() {
                    entry.target.classList.add('is-settled');
                    entry.target.removeEventListener('transitionend', handler);
                });
            });
        }, {
            // Start a little before the element arrives, so it has finished
            // moving by the time it is properly in view.
            rootMargin: '0px 0px -8% 0px',
            threshold: 0.02
        });

        targets.forEach(function (el, index) {
            el.setAttribute('data-reveal', '');

            // Stagger within a row of cards, then reset, so a long list does
            // not end up with a two-second delay at the bottom.
            var step = index % 4;
            if (step) el.style.transitionDelay = (step * 45) + 'ms';

            observer.observe(el);
        });

        // ---------------------------------------- reading progress
        var article = document.querySelector('.article-content');
        if (!article) return;

        var bar = document.createElement('div');
        bar.className = 'read-progress';
        bar.setAttribute('role', 'presentation');
        document.body.appendChild(bar);

        var ticking = false;

        function draw() {
            ticking = false;

            var rect = article.getBoundingClientRect();
            var start = rect.top + window.scrollY;
            // How far there is to read before the article's end reaches the
            // bottom of the viewport.
            var distance = article.offsetHeight - window.innerHeight;

            if (distance <= 0) {
                bar.style.transform = 'scaleX(0)';
                return;
            }

            var progress = (window.scrollY - start) / distance;
            progress = Math.min(1, Math.max(0, progress));

            bar.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
        }

        function schedule() {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(draw);
        }

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        draw();
    })();

/*
     * Give every code block a language label and a copy button.
     *
     * The editor stores a plain <pre><code class="language-xxx">; the chrome is
     * added here so old posts gain it too, and so no editor-only markup ever
     * has to be stripped out of the saved content.
     */
    (function () {
        var blocks = document.querySelectorAll('.article-content pre > code');
        if (!blocks.length) return;

        blocks.forEach(function (code) {
            var pre = code.parentElement;
            if (pre.parentElement && pre.parentElement.classList.contains('code-block')) return;

            var match = (code.className || '').match(/language-([\w+#-]+)/);
            var language = match ? match[1] : 'code';

            var wrapper = document.createElement('div');
            wrapper.className = 'code-block';

            var bar = document.createElement('div');
            bar.className = 'code-block-bar';

            var label = document.createElement('span');
            label.className = 'code-block-lang';
            label.textContent = language;

            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'code-block-copy';
            button.innerHTML = '<i class="bi bi-clipboard"></i><span>Copy</span>';

            bar.appendChild(label);
            bar.appendChild(button);

            pre.parentNode.insertBefore(wrapper, pre);
            wrapper.appendChild(bar);
            wrapper.appendChild(pre);

            button.addEventListener('click', function () {
                var text = code.innerText;

                var done = function (ok) {
                    button.classList.toggle('copied', ok);
                    button.querySelector('span').textContent = ok ? 'Copied' : 'Press Ctrl+C';
                    button.querySelector('i').className = ok ? 'bi bi-check2' : 'bi bi-clipboard';

                    setTimeout(function () {
                        button.classList.remove('copied');
                        button.querySelector('span').textContent = 'Copy';
                        button.querySelector('i').className = 'bi bi-clipboard';
                    }, 2000);
                };

                // navigator.clipboard needs a secure context, so keep a fallback
                // for plain-http sites.
                if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
                    return;
                }

                var helper = document.createElement('textarea');
                helper.value = text;
                helper.style.position = 'fixed';
                helper.style.opacity = '0';
                document.body.appendChild(helper);
                helper.select();

                try { done(document.execCommand('copy')); } catch (e) { done(false); }

                document.body.removeChild(helper);
            });
        });
    })();

/*
     * An advert slot is nothing until an advert is in it.
     *
     * A network advert is a promise, not a fact. AdSense returns nothing when
     * it has no inventory for a reader, an ad blocker removes the request
     * outright, and any of them can simply fail - and the slot is left holding
     * an "Advertisement" label over an empty bordered frame, which reads as a
     * broken page rather than as an absence.
     *
     * So nothing is drawn up front: no label, no frame, no reserved box, no
     * margins - see .tw-ad-wrapper:not([data-ad-filled]) in the stylesheet.
     * This module's first job is to open the slot the moment a creative is
     * actually put into it, which is the only honest signal that an advert
     * exists. Adverts of ours are printed with data-ad-filled already on them,
     * because an <img> written into the page cannot fail to arrive.
     *
     * Its second job is the other direction: a slot that opened and then turned
     * out to hold nothing after all. Two signals, because they arrive at
     * different times:
     *   - AdSense sets data-ad-status="unfilled" on its <ins>, which is the
     *     authoritative answer and can come at any moment, so it is watched.
     *   - Anything else that leaves the slot with no height is caught by a
     *     couple of later sweeps, which also covers a blocked script.
     *
     * Nothing is judged early: a slow advert is not an empty one.
     */
    (function () {
        var SETTLE_MS = [2500, 6000];
        var MIN_HEIGHT = 10;

        function bodyOf(wrapper) {
            return wrapper.querySelector('.tw-ad-body') || wrapper;
        }

        // What a network puts in a slot when it actually has an advert.
        var CREATIVE = 'iframe, img, ins, video, canvas, embed, object';

        /*
         * Is there really an advert in here?
         *
         * AdSense is asked rather than looked at. Its <ins> is in the markup
         * from the start, so the element being present proves nothing - what
         * proves it is data-ad-status, which AdSense sets to "filled" or
         * "unfilled" once it has decided. Looking for the element alone would
         * open every AdSense slot on the page before AdSense had answered.
         */
        function hasCreative(body) {
            var ins = body.querySelector('ins.adsbygoogle');

            if (ins) return ins.getAttribute('data-ad-status') === 'filled';

            return !!body.querySelector(CREATIVE);
        }

        /*
         * Open the slot: its label, its frame and the room it needs.
         *
         * The sticky containers are marked with it too. A rail or the bottom
         * bar is nothing but its advert, so an empty one is a fold tab hanging
         * in the margin with nothing behind it.
         */
        function markFilled(wrapper) {
            if (wrapper.hasAttribute('data-ad-filled')) return;

            wrapper.setAttribute('data-ad-filled', '');

            var sticky = wrapper.closest('[data-sticky-ad]');
            if (sticky) sticky.setAttribute('data-ad-filled', '');
        }

        /*
         * Watch a slot until something lands in it.
         *
         * Every slot gets one of these, not only the ones already judged empty:
         * a slot that has never been filled is exactly the one that must not be
         * drawn, and its creative can arrive at any moment - a second later on
         * a slow connection, or a minute later after a refresh.
         */
        function watchFill(wrapper) {
            var body = bodyOf(wrapper);

            if (hasCreative(body)) {
                markFilled(wrapper);

                return;
            }

            if (!('MutationObserver' in window)) return;

            new MutationObserver(function (mutations, observer) {
                if (!hasCreative(body)) return;

                markFilled(wrapper);
                observer.disconnect();
            }).observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-ad-status'] });
        }

        /*
         * A folded advert is not an empty one.
         *
         * Folding hides the advert's body, so it measures zero - and the sweep
         * below read that as "the network never filled this" and hid the whole
         * block, tab and all. The reader had folded it away and lost the way
         * back; reloading restored the fold from storage and did it again.
         */
        function folded(wrapper) {
            var sticky = wrapper.closest('[data-sticky-ad]');

            return !!(sticky && sticky.hasAttribute('data-folded'));
        }

        function collapse(wrapper) {
            /*
             * The phone's bottom bar is never closed from here.
             *
             * An advert that has not arrived yet, or a refresh that came back
             * with nothing, used to hide the whole bar - which on a phone
             * looked like the bar vanishing on its own, usually just as the
             * reader scrolled. It stays open; folding it away is the reader's
             * call, through the tab, and nobody else's.
             */
            if (wrapper.closest('[data-ad-always-open]')) return;

            wrapper.hidden = true;

            // One of these is nothing but its advert: an empty one would leave
            // a tab floating in the margin, or a bar of nothing along the
            // bottom of the window.
            var sticky = wrapper.closest('[data-sticky-ad]');
            if (sticky) sticky.hidden = true;

            watchForLateCreative(wrapper);
        }

        /*
         * A slow advert is not an empty one - but on a slow connection it can
         * arrive after the last sweep has already hidden its slot, and then it
         * was never seen. So a hidden slot keeps listening, and shows itself
         * again the moment a creative is put into it.
         *
         * Only what is ADDED counts. An image that failed to load is already
         * inside its slot; counting it would show the slot, the next sweep
         * would hide it again, and round it would go.
         *
         * AdSense's "unfilled" is a final answer, so that slot is left hidden.
         */
        function watchForLateCreative(wrapper) {
            if (!('MutationObserver' in window) || wrapper.hasAttribute('data-ad-watching')) return;

            var body = bodyOf(wrapper);
            var ins = body.querySelector('ins.adsbygoogle');
            if (ins && ins.getAttribute('data-ad-status') === 'unfilled') return;

            wrapper.setAttribute('data-ad-watching', '');

            var observer = new MutationObserver(function (mutations) {
                for (var i = 0; i < mutations.length; i++) {
                    var added = mutations[i].addedNodes;

                    for (var j = 0; j < added.length; j++) {
                        var node = added[j];
                        if (node.nodeType !== 1) continue;

                        if (node.matches(CREATIVE) || node.querySelector(CREATIVE)) {
                            wrapper.hidden = false;
                            wrapper.removeAttribute('data-ad-watching');
                            markFilled(wrapper);

                            // It came down with the advert, so it goes back up
                            // too - unless the reader folded it away meanwhile.
                            var sticky = wrapper.closest('[data-sticky-ad]');
                            if (sticky && !sticky.hasAttribute('data-folded')) sticky.hidden = false;

                            observer.disconnect();

                            return;
                        }
                    }
                }
            });

            observer.observe(body, { childList: true, subtree: true });
        }

        function isEmpty(wrapper) {
            var body = bodyOf(wrapper);

            // AdSense's own verdict wins whenever it has given one.
            var ins = body.querySelector('ins.adsbygoogle');
            if (ins && ins.getAttribute('data-ad-status') === 'unfilled') return true;

            // A fixed-size advert keeps its reserved box whether or not
            // anything arrived, so its height says nothing. What counts is
            // whether the network put a creative in it.
            if (body.classList.contains('tw-ad-body--sized')) {
                return !body.querySelector(CREATIVE);
            }

            return body.offsetHeight < MIN_HEIGHT;
        }

        /*
         * A slot whose room is kept while it waits (a sized advert - see the
         * stylesheet) is given this long after its snippet runs before it is
         * called empty. Closing it early and opening it again when a slow
         * creative lands moved the page twice: measured on a phone on the
         * live category page, 2026-10-12, a 728x90 arrived 12 seconds in.
         * The same ten seconds a refresh waits (FILL_WAIT_MS).
         */
        var RESERVED_WAIT_MS = 10000;

        function stillWaiting(wrapper) {
            var started = parseInt(wrapper.getAttribute('data-ad-started-at') || '0', 10);

            return !!started
                && !!wrapper.querySelector(':scope > .tw-ad-body--sized')
                && Date.now() - started < RESERVED_WAIT_MS;
        }

        function sweep() {
            // Not a slot still waiting for its snippet to be run - it has not
            // asked the network for anything yet. See "loading them after the
            // page", which marks it and lets go with ad:started.
            document.querySelectorAll('.tw-ad-wrapper:not([hidden]):not([data-ad-pending])')
                .forEach(function (wrapper) {
                    if (!folded(wrapper) && !stillWaiting(wrapper) && isEmpty(wrapper)) collapse(wrapper);
                });
        }

        /*
         * A snippet that has just been run gets the same settling time a slot
         * used to get from the moment the page was read - and a reserved one
         * gets RESERVED_WAIT_MS, with a last look when that is up.
         */
        document.addEventListener('ad:started', function (event) {
            var wrapper = event.detail && event.detail.slot;

            if (wrapper) {
                wrapper.setAttribute('data-ad-started-at', String(Date.now()));
                watchFill(wrapper);
            }

            SETTLE_MS.concat([RESERVED_WAIT_MS + 500]).forEach(function (delay) { window.setTimeout(sweep, delay); });
        });

        function watchAdsense() {
            if (!('MutationObserver' in window)) return;

            document.querySelectorAll('ins.adsbygoogle').forEach(function (ins) {
                var wrapper = ins.closest('.tw-ad-wrapper');
                if (!wrapper) return;

                new MutationObserver(function (mutations, observer) {
                    var status = ins.getAttribute('data-ad-status');

                    // Both answers are final, and either one ends the watch.
                    if (status === 'unfilled') {
                        collapse(wrapper);
                        observer.disconnect();
                    } else if (status === 'filled') {
                        markFilled(wrapper);
                        observer.disconnect();
                    }
                }).observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
            });
        }

        function start() {
            document.querySelectorAll('.tw-ad-wrapper:not([data-ad-filled])').forEach(watchFill);

            watchAdsense();
            SETTLE_MS.forEach(function (delay) { window.setTimeout(sweep, delay); });
        }

        /*
         * Unfolded again, long after the sweeps have run.
         *
         * Its advert is back on screen and has never been judged, so give it
         * the same settling time a fresh one gets before deciding it is empty.
         */
        document.addEventListener('sticky-ad:unfolded', function () {
            window.setTimeout(sweep, SETTLE_MS[0]);
        });

        /*
         * A refreshed advert has never been judged.
         *
         * The refresh replaces everything inside the slot, so the creative
         * watched a minute ago is gone and whatever replaced it is unproven. If
         * the network had nothing this time, the slot would otherwise sit there
         * holding the empty frame the refresh put in it.
         *
         * data-ad-filled is deliberately left on. Taking the space back and
         * giving it again would make the page jump on every single refresh; if
         * nothing did arrive, the sweep closes the slot once, after the same
         * settling time a fresh one gets.
         */
        document.addEventListener('ad:swapped', function (event) {
            var wrapper = event.detail && event.detail.slot;
            if (!wrapper || !wrapper.classList.contains('tw-ad-wrapper')) return;

            watchFill(wrapper);
            window.setTimeout(sweep, SETTLE_MS[0]);
        });

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', start);
        } else {
            start();
        }
    })();

/*
     * A name for every advert frame.
     *
     * Ad networks draw their creative in an <iframe> they create themselves,
     * usually after the page has loaded, and most never give it a title. A
     * screen reader then announces an unnamed frame, and Lighthouse marks the
     * page down for it (frame-title) on every page that carries such an ad.
     *
     * The frame is not ours to write, so it is named as it appears: whatever is
     * already in a slot when the page is ready, then anything the network adds
     * later. A frame the network did name - AdSense does - is left alone.
     */
    (function () {
        var SLOTS = '.tw-ad-wrapper';
        var LABEL = 'Advertisement';

        function nameFrames(root) {
            var frames = root.tagName === 'IFRAME' ? [root] : root.querySelectorAll('iframe');

            Array.prototype.forEach.call(frames, function (frame) {
                if (!frame.closest(SLOTS)) return;

                if (frame.getAttribute('title') || frame.getAttribute('aria-label') || frame.getAttribute('aria-labelledby')) {
                    return;
                }

                frame.setAttribute('title', LABEL);
            });
        }

        function start() {
            var slots = document.querySelectorAll(SLOTS);
            if (!slots.length) return;

            slots.forEach(nameFrames);

            if (!('MutationObserver' in window)) return;

            slots.forEach(function (slot) {
                new MutationObserver(function (mutations) {
                    mutations.forEach(function (mutation) {
                        mutation.addedNodes.forEach(function (node) {
                            if (node.nodeType === 1) nameFrames(node);
                        });
                    });
                }).observe(slot, { childList: true, subtree: true });
            });
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', start);
        } else {
            start();
        }
    })();

/*
     * Fit a fixed-size advert to a column narrower than it.
     *
     * A 728x90 banner does not fit a phone. The box around it already narrows
     * and keeps its proportions in CSS; this scales the creative down to match,
     * so the whole banner shows rather than its left two thirds.
     *
     * A transform, not a new width: the network's frame is laid out at the size
     * it was bought at, and a transform moves nothing else on the page.
     */
    (function () {
        var boxes = document.querySelectorAll('.tw-ad-body--sized .tw-html-ad');
        if (!boxes.length) return;

        function fit(box) {
            var width = parseFloat(getComputedStyle(box).getPropertyValue('--ad-w'));
            if (!width || !box.clientWidth) return;

            var scale = Math.min(1, box.clientWidth / width);

            if (scale < 1) {
                box.style.setProperty('--ad-scale', scale.toFixed(4));
            } else {
                box.style.removeProperty('--ad-scale');
            }
        }

        Array.prototype.forEach.call(boxes, fit);

        if ('ResizeObserver' in window) {
            var observer = new ResizeObserver(function (entries) {
                entries.forEach(function (entry) { fit(entry.target); });
            });

            Array.prototype.forEach.call(boxes, function (box) { observer.observe(box); });
        } else {
            window.addEventListener('resize', function () {
                Array.prototype.forEach.call(boxes, fit);
            });
        }
    })();

/*
     * How tall the masthead is, as a number the stylesheet can use.
     *
     * The side adverts start five pixels under it, and its height is its
     * contents rather than a number anybody wrote down - it changes with the
     * logo, the font, the screen, and again when the page scrolls and the bar
     * tightens up. So it is measured here and published as --nav-h.
     */
    (function () {
        var nav = document.querySelector('.glass-nav');
        if (!nav) return;

        var last = 0;

        function publish() {
            var height = Math.round(nav.getBoundingClientRect().height);

            // Only when it actually changes: this runs on scroll.
            if (height === last || height === 0) return;

            last = height;
            document.documentElement.style.setProperty('--nav-h', height + 'px');
        }

        publish();

        window.addEventListener('resize', publish, { passive: true });
        window.addEventListener('scroll', publish, { passive: true });

        // The web font arriving changes the height by a pixel or two.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(publish).catch(function () { /* nothing to do */ });
        }
    })();

    /*
     * The phone's bottom bar, measured.
     *
     * It is open all the time, so the page keeps room under its last line for
     * it (see the phone query in frontend.css). The room has to match the bar,
     * and the bar is not one height: a 320x100 advert makes it taller than a
     * 320x50, and folding it leaves only the tab. Published as --footer-bar-h
     * whenever it changes, the same way the header publishes --nav-h.
     */
    (function () {
        var bar = document.querySelector('.sticky-ad--mobile[data-ad-always-open]');
        if (!bar || !('ResizeObserver' in window)) return;

        var root = document.documentElement;

        function publish() {
            root.style.setProperty('--footer-bar-h', Math.ceil(bar.getBoundingClientRect().height) + 'px');
        }

        new ResizeObserver(publish).observe(bar);
        publish();
    })();

    /*
     * Folding a sticky advert away.
     *
     * The margins and the bottom bar are the places an advert follows the
     * reader down the page, so it has to be possible to be rid of one. It
     * folds rather than closes: a cross says "gone", and a reader who catches
     * it by accident - which near an advert is most of the accidental taps -
     * has no way back. The tab stays, turns over, and brings it back.
     *
     * The choice is kept for a day, per slot, in this browser only. There is
     * nothing here worth sending to a server, and a reader who clears their
     * browser starts fresh.
     */
    (function () {
        var sticky = document.querySelectorAll('[data-sticky-ad]');
        if (!sticky.length) return;

        var DAY = 864e5;
        var key = function (name) { return 'sticky-ad-folded:' + name; };

        function foldedUntil(name) {
            try {
                return parseInt(window.localStorage.getItem(key(name)), 10) || 0;
            } catch (e) {
                // Private browsing, or storage switched off: nothing is
                // remembered, which is a working site with a foldable advert.
                return 0;
            }
        }

        function remember(name, folded) {
            try {
                if (folded) {
                    window.localStorage.setItem(key(name), String(Date.now() + DAY));
                } else {
                    window.localStorage.removeItem(key(name));
                }
            } catch (e) { /* nothing to remember it with */ }
        }

        Array.prototype.forEach.call(sticky, function (slot) {
            var name = slot.getAttribute('data-sticky-ad');
            var button = slot.querySelector('[data-sticky-ad-toggle]');
            if (!button) return;

            function show(folded) {
                slot.toggleAttribute('data-folded', folded);
                button.setAttribute('aria-expanded', String(!folded));
                button.setAttribute('aria-label', folded ? 'Show this advert' : 'Fold this advert away');
            }

            show(foldedUntil(name) > Date.now());

            button.addEventListener('click', function () {
                var folded = !slot.hasAttribute('data-folded');

                show(folded);
                remember(name, folded);

                if (folded) return;

                /*
                 * Coming back: an advert hidden earlier for looking empty gets
                 * to show itself again, and the sweep is told to look at it
                 * once it has had time to fill.
                 */
                slot.hidden = false;

                slot.querySelectorAll('.tw-ad-wrapper[hidden]').forEach(function (wrapper) {
                    wrapper.hidden = false;
                });

                document.dispatchEvent(new CustomEvent('sticky-ad:unfolded'));
            });
        });
    })();

/*
     * A contents link that points inside a closed answer.
     *
     * The FAQ block is an accordion, and its questions are headings - so they
     * are in the article's own contents list. Clicking one would otherwise
     * scroll to a box that is shut, and the reader would see the question and
     * no answer. Whatever the link lands on is opened first, including a block
     * nested inside another.
     */
    (function () {
        function openTarget() {
            var id = decodeURIComponent((window.location.hash || '').slice(1));
            if (!id) return;

            var target = document.getElementById(id);
            if (!target || !target.closest) return;

            var box = target.closest('details');

            while (box) {
                box.open = true;
                box = box.parentElement ? box.parentElement.closest('details') : null;
            }

            // The browser had already scrolled, to where the shut box was.
            if (target.scrollIntoView) target.scrollIntoView({ block: 'start' });
        }

        window.addEventListener('hashchange', openTarget);

        if (window.location.hash) openTarget();
    })();

/*
     * The copy button on a command or an address.
     *
     * [copy] blocks in an article. The text beside the button is real,
     * selectable text, so a reader whose clipboard is blocked - or whose
     * browser never ran this - still gets the value by hand. The button only
     * ever adds speed, and says plainly when it could not.
     */
    (function () {
        document.addEventListener('click', function (event) {
            var button = event.target.closest('[data-copy-button]');
            if (!button) return;

            var line = button.closest('[data-copy-line]');
            var field = line && line.querySelector('[data-copy-text]');
            if (!field) return;

            var text = field.textContent.trim();
            var word = button.querySelector('.copy-line-word');
            var icon = button.querySelector('i');

            function done(ok) {
                button.classList.toggle('copied', ok);
                if (word) word.textContent = ok ? 'Copied' : 'Press Ctrl+C';
                if (icon) icon.className = ok ? 'bi bi-check2' : 'bi bi-clipboard';

                window.setTimeout(function () {
                    button.classList.remove('copied');
                    if (word) word.textContent = 'Copy';
                    if (icon) icon.className = 'bi bi-clipboard';
                }, 2000);
            }

            // navigator.clipboard needs a secure context; the fallback below is
            // what works on a plain-http install.
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });

                return;
            }

            var helper = document.createElement('textarea');
            helper.value = text;
            helper.style.position = 'fixed';
            helper.style.opacity = '0';
            document.body.appendChild(helper);
            helper.select();

            try { done(document.execCommand('copy')); } catch (e) { done(false); }

            document.body.removeChild(helper);
        });
    })();

/*
     * Newsletter sign-up.
     *
     * Submitted with fetch so the reader is not thrown to another page from
     * the footer. Without JavaScript the form posts normally and still works.
     */
    (function () {
        var form = document.querySelector('[data-subscribe]');
        if (!form) return;

        var message = form.querySelector('[data-subscribe-message]');
        var button = form.querySelector('button[type="submit"]');

        form.addEventListener('submit', function (event) {
            event.preventDefault();
            button.disabled = true;

            fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
                .then(function (response) {
                    return response.json().catch(function () { return {}; })
                        .then(function (body) { return { ok: response.ok, body: body }; });
                })
                .then(function (result) {
                    message.hidden = false;
                    message.classList.toggle('is-error', !result.ok);
                    message.textContent = result.body.message
                        || (result.ok ? 'Thanks.' : 'That did not work - please check the address.');

                    if (result.ok) form.reset();
                })
                .catch(function () {
                    message.hidden = false;
                    message.classList.add('is-error');
                    message.textContent = 'Network error - please try again.';
                })
                .then(function () { button.disabled = false; });
        });
    })();

/* ---------------------------------------------------- search box ----- */
(function () {
            const modal = document.getElementById('searchModal');
            if (!modal) return;

            const input = modal.querySelector('.search-input');
            const list = modal.querySelector('[data-search-results]');
            const status = modal.querySelector('[data-search-status]');
            const seeAll = modal.querySelector('[data-search-all]');
            const form = modal.querySelector('.search-form');

            const minLength = parseInt(input.dataset.min, 10);
            const endpoint = input.dataset.endpoint;
            const searchPage = input.dataset.searchPage;

            let debounce = null;
            let inFlight = null;
            let activeIndex = -1;

            // ------------------------------------------------ opening

            modal.addEventListener('shown.bs.modal', function () {
                // Not the autofocus attribute: it is applied while the modal is
                // still hidden, and the focus is lost when Bootstrap shows it.
                input.focus();
                input.select();
            });

            modal.addEventListener('hidden.bs.modal', function () {
                // Bootstrap returns focus to the trigger on its own.
                if (debounce) clearTimeout(debounce);
                if (inFlight) inFlight.abort();
            });

            // ------------------------------------------------ site search

            function escapeHtml(value) {
                const div = document.createElement('div');
                div.textContent = value == null ? '' : value;
                return div.innerHTML;
            }

            function render(payload) {
                list.innerHTML = '';
                activeIndex = -1;

                if (!payload.results.length) {
                    status.textContent = 'No results for "' + payload.query + '".';
                    seeAll.hidden = true;
                    return;
                }

                status.textContent = payload.total === 1
                    ? '1 result'
                    : payload.total + ' results';

                payload.results.forEach(function (item) {
                    const thumb = item.thumb
                        ? '<img class="search-thumb" src="' + escapeHtml(item.thumb) + '" alt="" loading="lazy">'
                        : '<span class="search-thumb search-thumb-empty" aria-hidden="true"></span>';

                    const meta = [item.category, item.date, item.reading_time ? item.reading_time + ' min read' : null]
                        .filter(Boolean)
                        .map(escapeHtml)
                        .join(' &middot; ');

                    // Every value is escaped: these come back from a search
                    // endpoint as data, and interpolating a title straight into
                    // markup is how a title containing a tag becomes script.
                    list.insertAdjacentHTML('beforeend',
                        '<li><a class="search-result" href="' + escapeHtml(item.url) + '">'
                        + thumb
                        + '<span class="search-text">'
                        + '<span class="search-result-title">' + escapeHtml(item.title) + '</span>'
                        + '<span class="search-result-meta">' + meta + '</span>'
                        + '</span></a></li>');
                });

                // Only worth offering when there is more to see.
                seeAll.hidden = payload.total <= payload.results.length;
                seeAll.href = searchPage + '?q=' + encodeURIComponent(payload.query);
            }

            function run(query) {
                // Abort the previous request rather than letting it land. A slow
                // answer for "ca" arriving after a fast one for "cache" would
                // otherwise replace the right results with stale ones.
                if (inFlight) inFlight.abort();
                inFlight = new AbortController();

                fetch(endpoint + '?q=' + encodeURIComponent(query), {
                    signal: inFlight.signal,
                    headers: { 'Accept': 'application/json' },
                })
                    .then(function (response) {
                        if (!response.ok) throw new Error(response.status);
                        return response.json();
                    })
                    .then(render)
                    .catch(function (error) {
                        if (error.name === 'AbortError') return;
                        status.textContent = 'Search is unavailable right now.';
                        list.innerHTML = '';
                    });
            }

            input.addEventListener('input', function () {
                const query = input.value.trim();

                if (debounce) clearTimeout(debounce);

                if (query.length < minLength) {
                    if (inFlight) inFlight.abort();
                    list.innerHTML = '';
                    seeAll.hidden = true;
                    status.textContent = query.length ? 'Keep typing…' : '';
                    return;
                }

                // Debounced, or every keystroke is a database query.
                debounce = setTimeout(function () { run(query); }, 250);
            });

            // ------------------------------------------------ keyboard

            function move(step) {
                const items = list.querySelectorAll('.search-result');
                if (!items.length) return;

                if (activeIndex >= 0) items[activeIndex].classList.remove('is-active');

                activeIndex = (activeIndex + step + items.length) % items.length;

                items[activeIndex].classList.add('is-active');
                items[activeIndex].scrollIntoView({ block: 'nearest' });
            }

            modal.addEventListener('keydown', function (event) {
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    move(1);
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    move(-1);
                } else if (event.key === 'Enter' && activeIndex >= 0) {
                    const items = list.querySelectorAll('.search-result');
                    if (items[activeIndex]) {
                        event.preventDefault();
                        items[activeIndex].click();
                    }
                }
            });

            // Submitting with nothing selected goes to the full search page.
            form.addEventListener('submit', function (event) {
                if (input.value.trim().length < minLength) event.preventDefault();
            });

            // ------------------------------------------------ tabs

            const tabs = modal.querySelectorAll('[data-search-tab]');
            const webPane = document.getElementById('searchPaneWeb');
            let googleLoaded = false;

            function loadGoogle(cx) {
                if (googleLoaded) return;
                googleLoaded = true;

                // parsetags:explicit stops the script rendering itself wherever
                // it likes; the callback puts it in our container instead.
                window.__gcse = {
                    parsetags: 'explicit',
                    callback: function () {
                        google.search.cse.element.render({
                            div: webPane.querySelector('[data-search-google]'),
                            tag: 'searchresults-only',
                        });
                        runGoogle();
                    },
                };

                const script = document.createElement('script');
                script.async = true;
                script.src = 'https://cse.google.com/cse.js?cx=' + encodeURIComponent(cx);
                document.head.appendChild(script);
            }

            function runGoogle() {
                const query = input.value.trim();
                if (query.length < minLength) return;

                const element = window.google
                    && google.search.cse.element.getAllElements()[0];

                if (element) element.execute(query);
            }

            tabs.forEach(function (tab) {
                tab.addEventListener('click', function () {
                    const target = tab.dataset.searchTab;

                    tabs.forEach(function (other) {
                        const on = other === tab;
                        other.classList.toggle('is-active', on);
                        other.setAttribute('aria-selected', on ? 'true' : 'false');
                    });

                    document.getElementById('searchPaneSite').hidden = target !== 'site';
                    if (webPane) webPane.hidden = target !== 'web';

                    if (target === 'web' && webPane) {
                        loadGoogle(webPane.dataset.searchCx);
                        if (googleLoaded && window.google) runGoogle();
                    }
                });
            });

            // A query typed on the site tab should carry over when the reader
            // switches, rather than opening onto an empty Google pane.
            input.addEventListener('input', function () {
                if (webPane && !webPane.hidden && googleLoaded) {
                    if (debounce) clearTimeout(debounce);
                    debounce = setTimeout(runGoogle, 400);
                }
            });
        })();

/* ---------------------------------------------------- announcement bar ----- */
(function () {
                const bar = document.getElementById('announce-bar');
                if (!bar) return;

                bar.querySelector('[data-announce-close]').addEventListener('click', function () {
                    bar.remove();
                });

                // ------------------------------------------------ copy a code

                bar.addEventListener('click', function (event) {
                    const button = event.target.closest('[data-announce-copy]');
                    if (!button) return;

                    const code = button.dataset.announceCopy;
                    const icon = button.querySelector('.bi');

                    function done() {
                        if (!icon) return;
                        icon.className = 'bi bi-check-lg';
                        setTimeout(function () { icon.className = 'bi bi-clipboard'; }, 1600);
                    }

                    if (navigator.clipboard && window.isSecureContext) {
                        navigator.clipboard.writeText(code).then(done).catch(fallback);
                    } else {
                        fallback();
                    }

                    // execCommand is deprecated but it is the only thing that
                    // works over plain http, which is where a local or
                    // intranet install lives.
                    function fallback() {
                        const field = document.createElement('textarea');
                        field.value = code;
                        field.setAttribute('readonly', '');
                        field.style.position = 'absolute';
                        field.style.left = '-9999px';
                        document.body.appendChild(field);
                        field.select();
                        try { document.execCommand('copy'); done(); } catch (e) { /* nothing to offer */ }
                        document.body.removeChild(field);
                    }
                });

                // ------------------------------------------------ rotation

                if (!bar.hasAttribute('data-announce-rotate')) return;

                const items = Array.from(bar.querySelectorAll('[data-announce-item]'));
                if (items.length < 2) return;

                // Somebody who has asked for less motion gets the messages
                // without the movement: they still rotate, they just cut.
                const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                if (reduced) bar.classList.add('is-instant');

                let index = 0;
                let timer = null;
                let paused = false;

                function advance() {
                    const current = items[index];
                    const nextIndex = (index + 1) % items.length;
                    const next = items[nextIndex];

                    // Out downwards, in from above - the movement reads as the
                    // bar scrolling from the top.
                    current.classList.remove('is-current');
                    current.classList.add('is-leaving');

                    next.classList.add('is-entering');

                    // Two frames, not one: the entering item needs to be painted
                    // at its starting offset before the class that animates it
                    // away is added, or the browser collapses both into one
                    // style change and nothing moves.
                    requestAnimationFrame(function () {
                        requestAnimationFrame(function () {
                            next.classList.remove('is-entering');
                            next.classList.add('is-current');
                        });
                    });

                    setTimeout(function () {
                        current.classList.remove('is-leaving');
                    }, 600);

                    index = nextIndex;
                }

                function start() {
                    if (timer || paused) return;
                    timer = setInterval(advance, 5000);
                }

                function stop() {
                    clearInterval(timer);
                    timer = null;
                }

                // Hovering holds the current message. Somebody reaching for a
                // promo code or a link should not have it move out from under
                // the pointer.
                bar.addEventListener('mouseenter', function () { paused = true; stop(); });
                bar.addEventListener('mouseleave', function () { paused = false; start(); });

                // The same for a keyboard: focus inside the bar holds it too.
                bar.addEventListener('focusin', function () { paused = true; stop(); });
                bar.addEventListener('focusout', function () {
                    if (!bar.contains(document.activeElement)) {
                        paused = false;
                        start();
                    }
                });

                // Nothing to animate on a hidden tab, and a backgrounded timer
                // just queues up transitions that all fire on return.
                document.addEventListener('visibilitychange', function () {
                    document.hidden ? stop() : start();
                });

                start();
            })();

/* ---------------------------------------------------- sidebar search ----- */
(function () {
                const form = document.querySelector('[data-sidebar-search]');
                const modal = document.getElementById('searchModal');

                // No modal on the page means no upgrade; the form still posts.
                if (!form || !modal || typeof bootstrap === 'undefined') return;

                const field = form.querySelector('input[name="q"]');
                const instance = bootstrap.Modal.getOrCreateInstance(modal);

                function open(event) {
                    event.preventDefault();

                    // Carry whatever was typed here into the box, so focusing
                    // the sidebar field and typing is not thrown away.
                    const target = modal.querySelector('.search-input');
                    if (target && field.value.trim()) {
                        target.value = field.value;
                        // The box only searches on input, and setting a value
                        // in script does not fire that.
                        target.dispatchEvent(new Event('input', { bubbles: true }));
                    }

                    field.blur();
                    instance.show();
                }

                // focus covers the keyboard route in as well as a click.
                field.addEventListener('focus', open);
                form.addEventListener('submit', open);
            })();

/* ---------------------------------------------------- hero media ----- */
// Swap the facade for the real player on click. Nothing from the
            // video provider is requested until this runs.
            document.addEventListener('click', function (event) {
                var facade = event.target.closest('[data-video-embed]');
                if (!facade) return;

                /*
                 * The video's own smart link, where one is set.
                 *
                 * Opened inside the click, so the browser counts it as
                 * something the reader asked for rather than a pop-up to be
                 * blocked - the same reason the network's direct link is opened
                 * this way. noopener, so the new tab gets no handle on this one,
                 * and nothing here tries to decide which tab ends up in front:
                 * the code that does that is exactly what a pop-under is.
                 *
                 * The video plays in this tab either way. A smart link that
                 * fails to open, or is blocked, must not cost the reader the
                 * thing they actually pressed play for.
                 */
                var smart = facade.dataset.smartLink;

                if (smart) {
                    try {
                        window.open(smart, '_blank', 'noopener');
                    } catch (e) { /* blocked: the video still plays */ }
                }

                var iframe = document.createElement('iframe');
                iframe.src = facade.dataset.videoEmbed;
                iframe.title = facade.getAttribute('aria-label') || 'Video player';
                iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
                /*
                 * Fullscreen, unless the facade says not to.
                 *
                 * The attribute is the half the browser enforces - a player
                 * given fs=0 still goes fullscreen if the frame is allowed to.
                 * Default true, so an article's own video is untouched; the
                 * video section sets it to 0.
                 */
                iframe.allowFullscreen = facade.dataset.allowFullscreen !== '0';
                iframe.referrerPolicy = 'strict-origin-when-cross-origin';

                facade.replaceWith(iframe);
            });

/* ------------------------------------------------------- cookie consent ----- */
/*
 * The banner's behaviour.
 *
 * Configuration arrives on window.consentSettings, written by the view,
 * because a bundled file cannot read a Blade value.
 *
 * The answer is stored in a cookie rather than localStorage: the server has to
 * know it too, so that server-side measurement is never sent for somebody who
 * declined it. localStorage is invisible to PHP.
 */
(function () {
    const bar = document.querySelector('[data-consent-bar]');
    const config = window.consentSettings;

    if (!config) return;

    function write(choice) {
        const expires = new Date(Date.now() + config.days * 864e5).toUTCString();

        // SameSite=Lax so the cookie survives a normal navigation from a search
        // result. It is not a credential, so it does not need Strict.
        document.cookie = config.cookie + '=' + encodeURIComponent(JSON.stringify(choice))
            + '; expires=' + expires + '; path=/; SameSite=Lax'
            + (location.protocol === 'https:' ? '; Secure' : '');
    }

    function signal(choice) {
        if (typeof gtag !== 'function') return;

        const ads = choice.ads ? 'granted' : 'denied';

        gtag('consent', 'update', {
            ad_storage: ads,
            ad_user_data: ads,
            ad_personalization: ads,
            analytics_storage: choice.analytics ? 'granted' : 'denied',
        });
    }

    function decide(choice) {
        write(choice);
        signal(choice);

        if (bar) bar.remove();

        /*
         * The non-Google tags are injected here rather than in the page.
         *
         * Consent Mode covers Google's own tags - they load and behave
         * according to the signals. Meta Pixel, Clarity and the UET tag honour
         * none of it, so they must not exist at all until they are allowed.
         */
        document.querySelectorAll('template[data-consent-tag]').forEach(function (template) {
            if (!choice[template.dataset.consentTag]) return;

            const fragment = template.content.cloneNode(true);

            // A <script> cloned from a template never executes; it has to be
            // recreated for the browser to run it.
            fragment.querySelectorAll('script').forEach(function (old) {
                const fresh = document.createElement('script');
                for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
                fresh.text = old.text;
                old.replaceWith(fresh);
            });

            document.head.appendChild(fragment);
        });
    }

    // Anything already decided is applied on load, so the tags a returning
    // reader allowed are there without them answering again.
    (function applyStored() {
        const match = document.cookie.match(new RegExp('(?:^|; )' + config.cookie + '=([^;]*)'));
        if (!match) return;

        try {
            const choice = JSON.parse(decodeURIComponent(match[1]));
            signal(choice);
            decide(choice);
        } catch (e) { /* a malformed cookie means ask again */ }
    })();

    if (!bar) return;

    const detail = bar.querySelector('[data-consent-detail]');
    const saveButton = bar.querySelector('[data-consent="save"]');
    const manageButton = bar.querySelector('[data-consent="manage"]');

    bar.addEventListener('click', function (event) {
        const action = event.target.closest('[data-consent]');
        if (!action) return;

        const all = {};
        config.categories.forEach(function (name) { all[name] = true; });

        const none = {};
        config.categories.forEach(function (name) { none[name] = false; });

        if (action.dataset.consent === 'accept') decide(all);
        if (action.dataset.consent === 'reject') decide(none);

        if (action.dataset.consent === 'manage') {
            detail.hidden = false;
            manageButton.hidden = true;
            saveButton.hidden = false;
            detail.querySelector('input').focus();
        }

        if (action.dataset.consent === 'save') {
            const chosen = {};

            bar.querySelectorAll('[data-consent-category]').forEach(function (box) {
                chosen[box.dataset.consentCategory] = box.checked;
            });

            decide(chosen);
        }
    });

    // Reopening from the footer link, so withdrawing is as easy as giving.
    document.addEventListener('click', function (event) {
        if (!event.target.closest('[data-consent-reopen]')) return;

        event.preventDefault();

        // Clearing the cookie and reloading brings the banner back rendered by
        // the server, rather than keeping a hidden copy on every page for the
        // few readers who ever use this.
        document.cookie = config.cookie + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        location.reload();
    });
})();

/* ------------------------------------------------------------------ *
 * Promo code
 *
 * Moved out of the [coupon] template: an article's rendered body is
 * cached as HTML, so a <script> pushed from inside it ran on the first
 * render and never again - leaving the copy button dead on every cached
 * article, which is every article after the first reader.
 * ------------------------------------------------------------------ */
/*
 * Copy the code.
 *
 * Delegated from the document, so a coupon that arrives with a
 * lazily loaded block works without rebinding. The button is in
 * the markup rather than created here: if this script never
 * runs, the code beside it is still plain selectable text.
 */
document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-coupon-copy]');
    if (!button) return;

    /*
     * The block this button belongs to, whichever it is: the [coupon] written
     * into an article, or one row of a saved offer's card. Both put the code in
     * a [data-coupon-code] beside their button, so one handler serves both.
     */
    var box = button.closest('.coupon, [data-offer-row]');
    var code = box && box.querySelector('[data-coupon-code]');
    if (!code) return;

    var label = button.querySelector('[data-coupon-copy-label]');
    var text = code.textContent.trim();

    function done() {
        button.setAttribute('data-copied', '');

        /*
         * The block as well as the button, so the code the reader was looking
         * at turns green too. Without it the only confirmation is on the button
         * - which on a phone is under the thumb that just pressed it.
         */
        box.setAttribute('data-copied', '');

        if (label) label.textContent = 'Copied';

        window.setTimeout(function () {
            button.removeAttribute('data-copied');
            box.removeAttribute('data-copied');
            if (label) label.textContent = 'Copy';
        }, 1800);
    }

    /*
     * navigator.clipboard needs a secure context, so it is
     * simply absent over plain http - which is where a staging
     * site usually lives. The selection fallback is not elegant
     * and it works everywhere.
     */
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(select);

        return;
    }

    select();

    function select() {
        var range = document.createRange();
        range.selectNodeContents(code);

        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        try {
            document.execCommand('copy');
            done();
        } catch (e) {
            // Left selected, so one more tap copies it by hand.
        }
    }
});

/* ------------------------------------------------------------------ *
 * Adverts: loading them after the page
 *
 * A network's snippet is a synchronous <script src>, and the browser
 * stops reading the page at every one until a third-party server has
 * answered. An article carried eight; on a phone over 4G the featured
 * image - the largest paint, the one Core Web Vitals scores - was found
 * seconds late because some of them stood above it (measured on the live
 * site, 2026-10-12: LCP 3.6-5.3s, and a third less with the adverts held
 * back).
 *
 * So the server prints each snippet inside <template data-ad-code>, where
 * nothing runs, and this puts them in place once the page's high-priority
 * pictures have loaded - or after HERO_WAIT_MS, whichever comes first.
 * One slot at a time, in page order, through the same queue a refresh
 * uses: Adsterra's snippets share one global, atOptions, and two running
 * at once swap sizes (see the refresh module below).
 *
 * A slot is marked data-ad-pending until its snippet has run, so the
 * empty-slot sweep does not judge an advert that has not been asked for.
 * ------------------------------------------------------------------ */
(function () {
    var SCRIPT_WAIT_MS = 8000;
    var HERO_WAIT_MS = 2500;

    var queue = Promise.resolve();

    function queued(task) {
        var run = queue.then(task, task);
        queue = run.catch(function () { /* the next one still gets its turn */ });

        return run;
    }

    /**
     * Run the scripts in a block, in order, the way a parser would have.
     *
     * A script written with innerHTML is inert, so each one is recreated. A
     * recreated external script is asynchronous unless told otherwise, and
     * the next inline one would run before it - which is the atOptions race
     * again inside a single advert. So each waits for the one before it.
     */
    function runScripts(container) {
        var chain = Promise.resolve();

        Array.prototype.forEach.call(container.querySelectorAll('script'), function (old) {
            chain = chain.then(function () {
                return new Promise(function (resolve) {
                    var script = document.createElement('script');

                    Array.prototype.forEach.call(old.attributes, function (attribute) {
                        script.setAttribute(attribute.name, attribute.value);
                    });

                    if (old.hasAttribute('src')) {
                        script.async = false;
                        script.onload = script.onerror = function () { resolve(); };
                        setTimeout(resolve, SCRIPT_WAIT_MS);
                    }

                    script.text = old.textContent;
                    old.parentNode.replaceChild(script, old);

                    if (!old.hasAttribute('src')) resolve();
                });
            });
        });

        return chain;
    }

    /**
     * Every <template data-ad-code> inside root, replaced by its markup.
     *
     * Through innerHTML, deliberately: scripts written that way are inert,
     * so nothing runs until runScripts() recreates them one at a time.
     * Cloning the template's content would run them at once, out of order.
     */
    function expand(root) {
        Array.prototype.forEach.call(root.querySelectorAll('template[data-ad-code]'), function (template) {
            var holder = document.createElement('div');
            holder.innerHTML = template.innerHTML;

            while (holder.firstChild) template.parentNode.insertBefore(holder.firstChild, template);
            template.parentNode.removeChild(template);
        });
    }

    window.TWAdScripts = { queued: queued, runScripts: runScripts, expand: expand };

    /** Once the high-priority pictures are in, or after HERO_WAIT_MS. */
    function afterHero(done) {
        var pending = Array.prototype.filter.call(
            document.querySelectorAll('img[fetchpriority="high"]'),
            function (img) { return !img.complete; }
        );
        var finished = false;
        var left = pending.length;

        function finish() {
            if (finished) return;
            finished = true;
            done();
        }

        if (!left) return finish();

        pending.forEach(function (img) {
            var one = function () { if (--left <= 0) finish(); };
            img.addEventListener('load', one, { once: true });
            img.addEventListener('error', one, { once: true });
        });

        setTimeout(finish, HERO_WAIT_MS);
    }

    function loadAll() {
        Array.prototype.forEach.call(document.querySelectorAll('template[data-ad-code]'), function (template) {
            var host = template.parentNode;
            var wrapper = host.closest('.tw-ad-wrapper');

            if (wrapper) wrapper.setAttribute('data-ad-pending', '');

            queued(function () {
                expand(host);

                return runScripts(host);
            }).then(function () {
                if (!wrapper) return;

                wrapper.removeAttribute('data-ad-pending');
                document.dispatchEvent(new CustomEvent('ad:started', { detail: { slot: wrapper } }));
            });
        });
    }

    function begin() {
        // Marked at once, so the sweep leaves them alone while they wait.
        Array.prototype.forEach.call(document.querySelectorAll('template[data-ad-code]'), function (template) {
            var wrapper = template.parentNode.closest('.tw-ad-wrapper');
            if (wrapper) wrapper.setAttribute('data-ad-pending', '');
        });

        afterHero(loadAll);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', begin);
    } else {
        begin();
    }
})();

/* ------------------------------------------------------------------ *
 * Adverts: counting what was seen, and asking for a new creative
 *
 * Both jobs need the same fact - is this advert on the reader's screen
 * right now - so both hang off one IntersectionObserver.
 *
 * Seen: counted once per advert per page view, and only after it has
 * been at least half visible for a moment. A page that merely carried
 * an advert nobody scrolled to has not shown it, and counting that
 * would be reporting a number no ad network recognises.
 *
 * Refresh: the interval is set per advert in the admin, and the clock
 * only runs while the advert is on screen. The page is never reloaded;
 * one block is fetched and swapped, so nothing else moves.
 * ------------------------------------------------------------------ */
(function () {
    var slots = document.querySelectorAll('[data-ad-seen]');
    if (!slots.length || !('IntersectionObserver' in window)) return;

    var csrf = document.querySelector('meta[name="csrf-token"]');
    var token = csrf ? csrf.getAttribute('content') : '';

    /** At least this much of the advert, for at least this long, counts as seen. */
    var VISIBLE_RATIO = 0.5;
    var SEEN_AFTER_MS = 1000;

    var state = new WeakMap();

    function stateFor(slot) {
        if (!state.has(slot)) {
            state.set(slot, {
                counted: false,
                seenTimer: null,
                refreshTimer: null,
                refreshing: false,
                // Refreshes that came back with nothing, in a row.
                failures: 0,
                gaveUp: false,
                // Whether the advert is on screen right now. Kept here rather
                // than asked for later: coming back to a tab has to restart the
                // clocks, and nothing fires an intersection entry for that.
                visible: false
            });
        }

        return state.get(slot);
    }

    function slug(slot) {
        return slot.getAttribute('data-ad') || '';
    }

    function countOnce(slot) {
        var own = stateFor(slot);
        if (own.counted || !slug(slot)) return;

        own.counted = true;

        var url = '/ad/' + encodeURIComponent(slug(slot)) + '/seen';

        // A beacon survives the reader leaving the page, which is exactly when
        // the last advert they saw would otherwise go uncounted.
        if (navigator.sendBeacon) {
            var form = new FormData();
            form.append('_token', token);

            if (navigator.sendBeacon(url, form)) return;
        }

        fetch(url, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': token, 'Accept': 'application/json' },
            keepalive: true
        }).catch(function () { /* a missed count is not worth telling anyone about */ });
    }

    /*
     * How a refresh is put on the page.
     *
     * It used to empty the slot and write the new advert in. Measured on the
     * live site with Adsterra: the bottom bar and the left rail came back
     * empty after their first refresh - the network often has nothing for the
     * same reader again half a minute later - so every one of them ran for
     * thirty seconds and then sat as an empty frame for the rest of the visit.
     *
     * Now the new advert is loaded out of sight, in a layer under the one the
     * reader is looking at, and only swapped in once a creative has actually
     * arrived. If nothing has after FILL_WAIT_MS, the layer is dropped and the
     * advert that was there stays. Nothing is moved when the swap happens: the
     * layer simply stops being hidden, because moving an iframe reloads it.
     *
     * One refresh at a time, page-wide. Adsterra's snippet sets a global,
     * atOptions, and its invoke.js reads it when it runs - which, for a script
     * added from JavaScript, is whenever it has downloaded. Two slots
     * refreshing a moment apart each read whichever atOptions was set last, and
     * a 728x90 turned up in a 160-wide rail. Queued, each snippet's own scripts
     * have run before the next one sets anything.
     */
    var FILL_WAIT_MS = 10000;
    var POLL_MS = 250;

    /** A slot that failed this many refreshes in a row stops trying for this page. */
    var GIVE_UP_AFTER = 2;

    // One queue and one script runner for the whole page, shared with the
    // first load - see "Adverts: loading them after the page" above. Two
    // queues would let a refresh and a first load set atOptions at once.
    var queued = window.TWAdScripts.queued;
    var runScripts = window.TWAdScripts.runScripts;

    /** Whether a creative has landed: a frame or picture with a size, or an AdSense unit marked filled. */
    function landed(container) {
        var adsense = container.querySelector('ins.adsbygoogle');
        if (adsense) return adsense.getAttribute('data-ad-status') === 'filled';

        return Array.prototype.some.call(
            container.querySelectorAll('iframe, img, video, canvas, embed, object'),
            function (el) { return el.offsetWidth > 1 && el.offsetHeight > 1; }
        );
    }

    /**
     * Load a fetched advert under the current one, and swap it in only once
     * it has arrived. Resolves true when it was swapped in.
     */
    function swap(slot, markup) {
        return new Promise(function (resolve) {
            var parsed = document.createElement('div');
            parsed.innerHTML = markup;

            var fresh = parsed.querySelector('[data-ad-seen]');
            var body = slot.querySelector('.tw-ad-body');
            if (!fresh || !body) return resolve(false);

            var freshBody = fresh.querySelector('.tw-ad-body') || fresh;
            var layer = document.createElement('div');
            layer.className = 'tw-ad-incoming';
            layer.innerHTML = freshBody.innerHTML;

            // The fetched block carries its snippet in a <template> like the
            // page does; out of it, so its scripts are seen and run below.
            window.TWAdScripts.expand(layer);

            if (getComputedStyle(body).position === 'static') body.style.position = 'relative';
            body.appendChild(layer);

            // An image or a promotional card is there the moment it is written;
            // only a snippet that runs code has anything to wait for.
            var waitForCreative = !!layer.querySelector('script');

            function swapIn() {
                Array.prototype.slice.call(body.childNodes).forEach(function (node) {
                    if (node !== layer) body.removeChild(node);
                });

                layer.classList.remove('tw-ad-incoming');
                resolve(true);
            }

            runScripts(layer).then(function () {
                if (!waitForCreative) return swapIn();

                var waited = 0;

                (function check() {
                    if (landed(layer)) return swapIn();

                    waited += POLL_MS;

                    if (waited >= FILL_WAIT_MS) {
                        body.removeChild(layer);

                        return resolve(false);
                    }

                    setTimeout(check, POLL_MS);
                })();
            });
        });
    }

    function refresh(slot) {
        var own = stateFor(slot);
        if (own.refreshing || own.gaveUp || !slug(slot)) return;

        own.refreshing = true;

        fetch('/ad/' + encodeURIComponent(slug(slot)) + '/refresh', {
            headers: { 'Accept': 'text/html' }
        })
            .then(function (response) { return response.ok ? response.text() : null; })
            .then(function (markup) {
                return markup ? queued(function () { return swap(slot, markup); }) : false;
            })
            .catch(function () { return false; })
            .then(function (swapped) {
                own.refreshing = false;

                if (swapped) {
                    own.failures = 0;
                    announceSwap(slot);
                } else if (++own.failures >= GIVE_UP_AFTER) {
                    // The network has nothing more for this reader right now;
                    // asking every half minute until they leave only costs
                    // them data. The advert already showing stays.
                    own.gaveUp = true;

                    return;
                }

                // The next interval starts from the creative now showing.
                start(slot);
            });
    }

    function start(slot) {
        var seconds = parseInt(slot.getAttribute('data-ad-refresh') || '0', 10);
        var own = stateFor(slot);

        if (!seconds || own.refreshTimer || own.gaveUp) return;

        own.refreshTimer = setTimeout(function () {
            own.refreshTimer = null;
            refresh(slot);
        }, seconds * 1000);
    }

    function stop(slot) {
        var own = stateFor(slot);

        if (own.refreshTimer) {
            clearTimeout(own.refreshTimer);
            own.refreshTimer = null;
        }
    }

    var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            var slot = entry.target;
            var own = stateFor(slot);
            var visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;

            own.visible = visible;

            if (visible) {
                if (!own.counted && !own.seenTimer) {
                    // A moment on screen, not a scroll straight past it.
                    own.seenTimer = setTimeout(function () {
                        own.seenTimer = null;
                        countOnce(slot);
                    }, SEEN_AFTER_MS);
                }

                start(slot);

                return;
            }

            if (own.seenTimer) {
                clearTimeout(own.seenTimer);
                own.seenTimer = null;
            }

            // Out of sight: the timer waits rather than running down, so an
            // advert is never replaced while nobody is looking at it.
            stop(slot);
        });
    }, { threshold: [0, VISIBLE_RATIO, 1] });

    slots.forEach(function (slot) {
        // A slot hidden for this screen size has no width or height, so it
        // never becomes visible and is neither counted nor refreshed.
        observer.observe(slot);
    });

    /*
     * A tab in the background is not a reader: hold every clock until it is.
     *
     * And start them again when it comes back, which is the half that was
     * missing. IntersectionObserver reports a CHANGE in intersection, and
     * returning to a tab changes nothing about where an advert sits - so no
     * entry ever fired, start() was never called, and refreshing was dead for
     * the rest of that page's life. One switch to another tab and back was
     * enough to do it, which is exactly what somebody does while testing the
     * interval they have just set.
     */
    document.addEventListener('visibilitychange', function () {
        slots.forEach(function (slot) {
            if (document.hidden) {
                stop(slot);

                return;
            }

            if (stateFor(slot).visible) start(slot);
        });
    });

    /*
     * A refreshed advert is judged again.
     *
     * swap() replaces everything inside the slot, so the creative the collapse
     * module was watching is gone and the one that replaced it has never been
     * looked at. If the network had nothing this time, the slot would sit there
     * holding the empty frame the refresh put in it.
     */
    function announceSwap(slot) {
        document.dispatchEvent(new CustomEvent('ad:swapped', { detail: { slot: slot } }));
    }
})();

/* ------------------------------------------------------------------ *
 * The ad network's direct link
 *
 * Opening an article from a listing opens two things: the article in
 * the tab the reader is already in, and the network's link in a new
 * one. The article's own navigation is never touched - the reader gets
 * what they clicked, which is the whole difference between this and
 * the tricks that get a site flagged.
 *
 * The page only carries the address when it may run at all: switched
 * on, on a listing, and AdSense off. See direct-link.blade.php - the
 * decision is made there, not here, so there is one place to read it.
 *
 * At most once an hour per reader. A link on every click is what makes
 * somebody install a blocker, and it is what an ad network calls
 * invalid traffic.
 * ------------------------------------------------------------------ */
(function () {
    var config = document.querySelector('[data-direct-link]');
    if (!config) return;

    var url = config.getAttribute('data-direct-link');
    var minutes = parseInt(config.getAttribute('data-direct-link-minutes') || '60', 10);
    if (!url) return;

    var KEY = 'direct-link-shown';

    function shownRecently() {
        try {
            var last = parseInt(window.localStorage.getItem(KEY), 10) || 0;

            return Date.now() - last < minutes * 60000;
        } catch (e) {
            // Storage switched off: the guard cannot be kept, so the link is
            // left alone rather than shown on every single click.
            return true;
        }
    }

    function remember() {
        try {
            window.localStorage.setItem(KEY, String(Date.now()));
        } catch (e) { /* nothing to remember it with */ }
    }

    document.addEventListener('click', function (event) {
        // Only a plain left click on an article's own link. A middle click, or
        // one with a modifier, is the reader asking for a tab of their own, and
        // answering that with an advert would be answering a different question.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        var link = event.target.closest('.post-title a, .post-card a[href]');
        if (!link || !link.href) return;

        // Somewhere else entirely - a share button, another site - is not an
        // article being opened.
        if (link.origin !== window.location.origin) return;

        if (shownRecently()) return;

        remember();

        /*
         * Opened inside the click, so the browser counts it as something the
         * reader asked for rather than a pop-up to be blocked.
         *
         * noopener: the new tab gets no handle on this one. Which tab the
         * browser puts in front is its decision and the site does not fight it
         * - the code that does is exactly what a pop-under is.
         */
        window.open(url, '_blank', 'noopener');

        // The article's own link is left to do what it was going to do.
    });
})();

/* ------------------------------------------------------------------ *
 * The rest of a category's tags
 *
 * The row under an article carries every tag used in its category -
 * fifteen of them, and a button for the rest. A category with eighty
 * would otherwise put a wall of them between the article and the
 * comments.
 *
 * All of them are in the page from the start, the extras carrying the
 * hidden attribute: a crawler and a reader with no script both get the
 * whole set, and the button is the only thing script is needed for.
 * ------------------------------------------------------------------ */
(function () {
    var button = document.querySelector('[data-tag-more]');
    if (!button) return;

    var rest = document.querySelector('[data-tag-rest]');
    if (!rest) return;

    button.addEventListener('click', function () {
        rest.hidden = false;

        // The button has done its one job. Removing it rather than leaving a
        // dead control in the row - there is no second press.
        button.remove();
    });
})();

/* ------------------------------------------------------------------ *
 * Back to top
 *
 * Shown once the reader is most of a screen down, and kept clear of
 * everything else fixed to the window: it steps up above the bottom
 * advert bar and the cookie banner, and left of the right-hand rail
 * when the two would meet. Those change size on their own - an advert
 * arriving, a bar folded, the banner accepted - so it is placed again
 * whenever any of them does, not only on resize.
 * ------------------------------------------------------------------ */
(function () {
    var button = document.querySelector('[data-to-top]');
    if (!button) return;

    var GAP = 16;
    var CLEARANCE = 12;
    var BOTTOM_THINGS = '.sticky-ad--footer, [data-consent-bar]';

    function update() {
        button.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
    }

    function shown(el) {
        if (el.hidden) return null;

        var r = el.getBoundingClientRect();

        return r.width > 0 && r.height > 0 ? r : null;
    }

    function place() {
        var height = window.innerHeight;
        var width = document.documentElement.clientWidth;
        var bottom = GAP;
        var right = GAP;

        // Above anything along the foot of the window.
        Array.prototype.forEach.call(document.querySelectorAll(BOTTOM_THINGS), function (el) {
            var r = shown(el);
            if (r && r.top < height) bottom = Math.max(bottom, height - r.top + CLEARANCE);
        });

        // Beside the right-hand rail, where the two would overlap.
        var rail = document.querySelector('.side-rail--right');
        var railBox = rail && shown(rail);
        var size = button.offsetHeight || 44;
        var top = height - bottom - size;

        if (railBox && railBox.left < width - GAP && railBox.top < top + size && railBox.bottom > top) {
            right = width - railBox.left + CLEARANCE;
        }

        button.style.bottom = bottom + 'px';
        button.style.right = right + 'px';
    }

    button.addEventListener('click', function (event) {
        event.preventDefault();

        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' });

        // A keyboard reader goes back up with it, rather than being left on a
        // button at the foot of a page they are no longer looking at.
        var home = document.querySelector('.navbar-brand');
        if (home) home.focus({ preventScroll: true });
    });

    var pending = false;

    window.addEventListener('scroll', function () {
        if (pending) return;

        pending = true;
        window.requestAnimationFrame(function () {
            pending = false;
            update();
        });
    }, { passive: true });

    window.addEventListener('resize', function () {
        update();
        place();
    }, { passive: true });

    var watched = Array.prototype.slice.call(document.querySelectorAll(BOTTOM_THINGS + ', .side-rail--right'));

    if ('ResizeObserver' in window) {
        var sizes = new ResizeObserver(place);
        watched.forEach(function (el) { sizes.observe(el); });
    }

    // Shown, hidden or folded without changing size the way ResizeObserver
    // sees it: the banner dismissed, a bar hidden because nothing filled it.
    if ('MutationObserver' in window) {
        var states = new MutationObserver(place);
        watched.forEach(function (el) {
            states.observe(el, { attributes: true, attributeFilter: ['hidden', 'data-folded', 'class', 'style'] });
        });
    }

    update();
    place();
})();
